/**
 * @file Firebase Auth integration surface. Wraps the SDK's sign-up / sign-in / sign-out /
 * email-update / password-reset / disclaimer-acceptance flows behind a stable interface
 * that takes the calling component's `toast`, `router`, and `t` so this module never reaches
 * into global singletons.
 *
 * Routing convention: every successful auth flow pushes to the `games` route (the post-auth
 * landing). Failures are surfaced as toasts; only the error codes the product cares about
 * (e.g. `auth/email-already-in-use`, `auth/invalid-email`, `auth/requires-recent-login`)
 * get bespoke messages — anything else falls through to a generic error key.
 */

import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  GoogleAuthProvider,
  FacebookAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateEmail,
  type User,
  type UserCredential
} from 'firebase/auth'

import { changeUserEmail, acceptDisclaimer } from '@/stores/users'

import { type Router } from 'vue-router'
import { type ToastInterface } from 'vue-toastification'

interface AuthService {
  emailSignUp: (
    email: string,
    pwd: string,
    toast: ToastInterface,
    router: Router,
    t: (key: string) => string
  ) => void
  emailSignIn: (
    email: string,
    pwd: string,
    toast: ToastInterface,
    router: Router,
    t: (key: string) => string
  ) => void
  googleSignIn: (toast: ToastInterface, router: Router, t: (key: string) => string) => void
  facebookSignIn: (toast: ToastInterface, router: Router, t: (key: string) => string) => void
  getSignedInUser: () => Promise<User | null>
  signOut: () => Promise<void>
  sendResetPasswordEmail: (email: string, toast: ToastInterface, t: (key: string) => string) => void
  updateEmail: (email: string, toast: ToastInterface, t: (key: string) => string) => void
  acceptDisclaimer: (toast: ToastInterface, t: (key: string) => string) => void
}

const authService: AuthService = {
  /**
   * Creates a Firebase Auth user with email + password and immediately fires a verification
   * email. On success routes to `games`. On `auth/email-already-in-use` shows a dedicated
   * toast; every other error path falls back to the generic sign-in error.
   */
  emailSignUp: (email, pwd, toast, router, t) => {
    createUserWithEmailAndPassword(getAuth(), email, pwd)
      .then((userCredential: UserCredential) => {
        sendEmailVerification(userCredential.user).then(() => {
          router.push({ name: 'games' })
        })
      })
      .catch((error) => {
        console.error(error.code, error.message)
        if (error.code === 'auth/email-already-in-use')
          toast.error(t('pages.auth.sign-in.email-already-used'))
        else toast.error(t('pages.auth.sign-in.generic-error'))
      })
  },
  /** Email + password sign-in. Surfaces `auth/invalid-email` as a dedicated toast; all other errors map to the generic message. */
  emailSignIn: (email, pwd, toast, router, t) => {
    signInWithEmailAndPassword(getAuth(), email, pwd)
      .then(() => {
        router.push({ name: 'games' })
      })
      .catch((error) => {
        console.error(error.code, error.message)
        if (error.code === 'auth/invalid-email') toast.error(t('pages.auth.sign-in.invalid-email'))
        else toast.error(t('pages.auth.sign-in.generic-error'))
      })
  },
  /** Google OAuth popup sign-in. Errors are coalesced into the generic toast — provider-specific recovery is not implemented. */
  googleSignIn: (toast, router, t) => {
    signInWithPopup(getAuth(), new GoogleAuthProvider())
      .then(() => {
        router.push({ name: 'games' })
      })
      .catch((error) => {
        console.error(error.code, error.message)
        toast.error(t('pages.auth.sign-in.generic-error'))
      })
  },
  /** Facebook OAuth popup sign-in. Same coalesced-error behaviour as `googleSignIn`. */
  facebookSignIn: (toast, router, t) => {
    signInWithPopup(getAuth(), new FacebookAuthProvider())
      .then(() => {
        router.push({ name: 'games' })
      })
      .catch((error) => {
        console.error(error.code, error.message)
        toast.error(t('pages.auth.sign-in.generic-error'))
      })
  },
  /**
   * Resolves with the currently signed-in `User` (or `null` when the listener fires with no
   * user). Wraps `onAuthStateChanged` into a one-shot promise — used by `useAuthStore.getSignedInUser`
   * to hydrate state on app boot. Note: the listener is not unsubscribed; this is acceptable
   * because it is called at most once per session.
   */
  getSignedInUser: () => {
    return new Promise<User | null>((resolve) => {
      onAuthStateChanged(getAuth(), (user) => {
        if (!user) resolve(null)
        else resolve(user)
      })
    })
  },
  /** Signs the user out of Firebase. Callers are responsible for navigating away from auth-gated routes after the promise resolves. */
  signOut: () => {
    return signOut(getAuth())
  },
  /** Fires a Firebase password-reset email. On success shows the `reset-password-email-sent` toast; on `auth/invalid-email` shows the invalid-email toast. */
  sendResetPasswordEmail: (email, toast, t) => {
    sendPasswordResetEmail(getAuth(), email)
      .then(() => {
        toast.success(t('pages.parameters.reset-password-email-sent'))
      })
      .catch((error) => {
        console.error(error.code, error.message)
        if (error.code === 'auth/invalid-email') toast.error(t('pages.auth.sign-in.invalid-email'))
        else toast.error(t('pages.auth.sign-in.generic-error'))
      })
  },
  /**
   * Three-step email change:
   *  1. `updateEmail` on the Firebase Auth user.
   *  2. `sendEmailVerification` to the new address.
   *  3. Mirror the new email into the Firestore `users/{uid}` document via `changeUserEmail`.
   *
   * Surfaces dedicated toasts for `auth/invalid-email`, `auth/email-already-in-use`, and
   * `auth/requires-recent-login` (the user must re-authenticate). Any other failure
   * falls through to the generic email-change-error toast.
   */
  updateEmail: (email, toast, t) => {
    const user = getAuth().currentUser

    if (!user) {
      toast.error(t('pages.parameters.messages.email-change-generic-error'))
      return
    }

    updateEmail(user, email)
      .then(() => {
        const user = getAuth().currentUser
        if (!user) {
          toast.error(t('pages.parameters.messages.email-change-generic-error'))
          return
        }
        sendEmailVerification(user).then(async () => {
          if (!user.email) {
            toast.error(t('pages.parameters.messages.email-change-generic-error'))
            return
          }
          changeUserEmail(user.uid, user.email).then(() => {
            toast.success(t('pages.parameters.email-updated'))
          })
        })
      })
      .catch((error) => {
        console.error(error.code, error.message)
        if (error.code === 'auth/invalid-email') toast.error(t('pages.auth.sign-in.invalid-email'))
        if (error.code === 'auth/email-already-in-use')
          toast.error(t('pages.auth.sign-in.email-already-used'))
        if (error.code === 'auth/requires-recent-login')
          toast.error(t('pages.parameters.requires-recent-login'))
        else toast.error(t('pages.parameters.messages.email-change-generic-error'))
      })
  },
  /**
   * Persists in-game disclaimer acceptance against the signed-in user's Firestore profile.
   * Refuses (with a toast) if no current user is signed in — the disclaimer flow must run
   * inside an authenticated session.
   */
  acceptDisclaimer: (toast, t) => {
    const user = getAuth().currentUser

    if (!user) {
      toast.error(t('pages.disclaimer.generic-error'))
      return
    }

    acceptDisclaimer(user.uid).catch((error) => {
      console.error(error.code, error.message)
      toast.error(t('pages.disclaimer.generic-error'))
    })
  }
}

export default authService
